import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { DefaultValuesService } from 'src/app/services/defaultvalues.service';
import { InvestmentControllingService } from 'src/app/services/investment-controlling.service';
import { IncoOplMeasures } from 'src/app/interfaces/investmentcontrolling/inco-opl-measures';
import { IncoMeasureUpdateRequest } from 'src/app/interfaces/investmentcontrolling/inco-measure-update';
import { IncoOplReportingResponse } from 'src/app/interfaces/investmentcontrolling/inco-opl-reporting-response';
import { IncoOplPeriodsInfo } from 'src/app/interfaces/investmentcontrolling/inco-opl-periods-info';
import { ToastrService } from 'ngx-toastr';
import { DialogService } from 'src/app/services/dialog.service';
import { TableColumn } from '../../shared-components/data-table/data-table.component';
import { TableUtils } from 'src/app/utils/table-utils';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { ChangelogDialogComponent, ChangelogDialogConfig } from '../../shared-components/changelog-dialog/changelog-dialog.component';
import { Subject, merge } from 'rxjs';
import { takeUntil, filter, startWith, debounceTime } from 'rxjs/operators';
import { isPhaseEditableForPlanning } from 'src/app/constants/inco-phases';
import { Router } from '@angular/router';
import { Utils } from 'src/app/utils/utils';


type OplForecastType = 'FC' | 'BUD' | 'PL1' | 'PL2';

@Component({
  selector: 'app-inco-oplreporting',
  templateUrl: './inco-oplreporting.component.html',
  styleUrls: ['./inco-oplreporting.component.less', '../../../../style/new-generic-styles.component.less'],
  standalone: false
})
export class IncoOplreportingComponent implements OnInit, OnDestroy {
  isExcelModeEnabled: boolean = false;
  title: string = '';
  isLoading: boolean = false;
  isEditModeEnabled: boolean = false;

  projects: IncoOplMeasures[] = [];
  tableData: any[] = [];
  originalProjects: IncoOplMeasures[] = [];

  updatedMeasures: Map<string, { projectId: number; value: number | null; forecastType: OplForecastType }> = new Map();

  periods: IncoOplPeriodsInfo | null = null;

  sapFcTotal: number = 0;
  sapBudTotal: number = 0;
  sapPl1Total: number = 0;
  sapPl2Total: number = 0;

  currencyShort: string = 'EUR';
  currencySymbol: string = '€';
  decimalDigits: number = 2;

  verificationStatus: 'success' | 'error' | '' = '';
  isVerified: boolean = false;

  columnValidationStatus: Map<string, 'valid' | 'invalid' | null> = new Map();

  tableColumns: TableColumn[] = [];

  isRowEditableFn = (row: any, _rowIndex: number): boolean => {
    if (this.defaults.isMersyAdminINCO) {
      return true;
    }
    return isPhaseEditableForPlanning(row.phaseId);
  };

  private readonly destroy$ = new Subject<void>();

  constructor(
    public defaults: DefaultValuesService,
    private readonly incoService: InvestmentControllingService,
    private readonly toastrService: ToastrService,
    private readonly dialog: DialogService,
    private readonly http: HttpClient,
    @Inject('BASE_URL') private readonly baseUrl: string,
    private readonly matDialog: MatDialog,
    private readonly router: Router
  ) {
    this.initializeColumns();
  }

  ngOnInit(): void {
    this.updateTitle();
    this.setupSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSubscriptions(): void {
    merge(
      this.defaults.managementIdMessageChanges$,
      this.defaults.fiscalYearMessageChanges$
    ).pipe(
      startWith(null),
      debounceTime(50),
      filter(() => this.isCurrentManagementIdValidForInco()),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.isEditModeEnabled = false;
      this.isVerified = false;
      this.verificationStatus = '';
      this.loadData();
    });
  }

  private isCurrentManagementIdValidForInco(): boolean {
    const currentNode = this.findNodeByValue(this.defaults.managementids, this.defaults.managementid);
    return currentNode?.BusinessLevel === 'MANAGEMENT' || currentNode?.BusinessLevel === 'FIELDBRANCH';
  }

  private findNodeByValue(nodes: any[], value: string): any | null {
    for (const node of nodes) {
      if (node.Value === value) return node;
      if (node.Groups?.length > 0) {
        const found = this.findNodeByValue(node.Groups, value);
        if (found) return found;
      }
    }
    return null;
  }

  private initializeColumns(): void {
    const fcYearLabel = this.periods?.fc?.label || '';
    const budYearLabel = this.periods?.bud?.label || '';
    const pl1YearLabel = this.periods?.pl1?.label || '';
    const pl2YearLabel = this.periods?.pl2?.label || '';

    const baseColumns: TableColumn[] = [
      {
        key: 'projectId',
        header: 'Project ID',
        width: '5%',
        align: 'left',
        editable: false,
        formatter: (value, row, index) => index === 0 ? 'TOTAL' : value?.toString() || '-'
      },
      {
        key: 'projectTitle',
        header: 'Project Title',
        width: '15%',
        align: 'left',
        editable: false,
        formatter: (value, _row, index) => index === 0 ? '-' : value || '-'
      },
      {
        key: 'maintenance',
        header: 'Maintenance',
        headerSub: '%',
        width: '4%',
        align: 'center',
        editable: false,
        formatter: (value, _row, index) => index === 0 ? '-' : TableUtils.formatDecimal(value, TableUtils.percentDecimals)
      },
      {
        key: 'expansion',
        header: 'Expansion',
        headerSub: '%',
        width: '4%',
        align: 'center',
        editable: false,
        formatter: (value, _row, index) => index === 0 ? '-' : TableUtils.formatDecimal(value, TableUtils.percentDecimals)
      },
      {
        key: 'phase',
        header: 'Phase',
        width: '4%',
        align: 'left',
        editable: false,
        formatter: (value, _row, index) => index === 0 ? '-' : value || '-'
      },
      {
        key: 'currency',
        header: 'Currency',
        width: '1%',
        align: 'left',
        editable: false,
        formatter: (_value, _row, _index) => this.currencyShort || '-'
      }
    ];

    const forecastColumns: TableColumn[] = [
      {
        key: 'fcValue',
        header: 'FC',
        headerSub: fcYearLabel,
        width: '8%',
        align: 'right',
        type: 'currency',
        editable: true,
        formatter: (value, _row, index) => {
          if (index === 0) {
            return this.formatCurrency(this.getTotalFcValue());
          }
          return this.formatCurrency(value);
        },
        cellClass: (_value, _row, index) => {
          if (index === 0 && this.columnValidationStatus.has('fcValue')) {
            const status = this.columnValidationStatus.get('fcValue');
            return this.getValidationClass(status);
          }
          return null;
        }
      },
      {
        key: 'fcSapValue',
        header: 'FC SAP',
        headerSub: fcYearLabel,
        width: '8%',
        align: 'right',
        type: 'currency',
        editable: false,
        formatter: (_value, _row, index) => {
          if (index === 0) {
            return this.formatCurrency(this.sapFcTotal);
          }
          return '-';
        },
        cellClass: (_value, _row, index) => {
          if (index !== undefined && index > 0) {
            return 'readonly-cell';
          }
          return null;
        }
      },
      {
        key: 'budValue',
        header: 'BUD',
        headerSub: budYearLabel,
        width: '8%',
        align: 'right',
        type: 'currency',
        editable: true,
        formatter: (value, _row, index) => {
          if (index === 0) {
            return this.formatCurrency(this.getTotalBudValue());
          }
          return this.formatCurrency(value);
        },
        cellClass: (_value, _row, index) => {
          if (index === 0 && this.columnValidationStatus.has('budValue')) {
            const status = this.columnValidationStatus.get('budValue');
            return this.getValidationClass(status);
          }
          return null;
        }
      },
      {
        key: 'budSapValue',
        header: 'BUD SAP',
        headerSub: budYearLabel,
        width: '8%',
        align: 'right',
        type: 'currency',
        editable: false,
        formatter: (_value, _row, index) => {
          if (index === 0) {
            return this.formatCurrency(this.sapBudTotal);
          }
          return '-';
        },
        cellClass: (_value, _row, index) => {
          if (index !== undefined && index > 0) {
            return 'readonly-cell';
          }
          return null;
        }
      },
      {
        key: 'pl1Value',
        header: 'PL1',
        headerSub: pl1YearLabel,
        width: '8%',
        align: 'right',
        type: 'currency',
        editable: true,
        formatter: (value, _row, index) => {
          if (index === 0) {
            return this.formatCurrency(this.getTotalPl1Value());
          }
          return this.formatCurrency(value);
        },
        cellClass: (_value, _row, index) => {
          if (index === 0 && this.columnValidationStatus.has('pl1Value')) {
            const status = this.columnValidationStatus.get('pl1Value');
            return this.getValidationClass(status);
          }
          return null;
        }
      },
      {
        key: 'pl1SapValue',
        header: 'PL1 SAP',
        headerSub: pl1YearLabel,
        width: '8%',
        align: 'right',
        type: 'currency',
        editable: false,
        formatter: (_value, _row, index) => {
          if (index === 0) {
            return this.formatCurrency(this.sapPl1Total);
          }
          return '-';
        },
        cellClass: (_value, _row, index) => {
          if (index !== undefined && index > 0) {
            return 'readonly-cell';
          }
          return null;
        }
      },
      {
        key: 'pl2Value',
        header: 'PL2',
        headerSub: pl2YearLabel,
        width: '8%',
        align: 'right',
        type: 'currency',
        editable: true,
        formatter: (value, _row, index) => {
          if (index === 0) {
            return this.formatCurrency(this.getTotalPl2Value());
          }
          return this.formatCurrency(value);
        },
        cellClass: (_value, _row, index) => {
          if (index === 0 && this.columnValidationStatus.has('pl2Value')) {
            const status = this.columnValidationStatus.get('pl2Value');
            return this.getValidationClass(status);
          }
          return null;
        }
      },
      {
        key: 'pl2SapValue',
        header: 'PL2 SAP',
        headerSub: pl2YearLabel,
        width: '8%',
        align: 'right',
        type: 'currency',
        editable: false,
        formatter: (_value, _row, index) => {
          if (index === 0) {
            return this.formatCurrency(this.sapPl2Total);
          }
          return '-';
        },
        cellClass: (_value, _row, index) => {
          if (index !== undefined && index > 0) {
            return 'readonly-cell';
          }
          return null;
        }
      }
    ];

    this.tableColumns = [...baseColumns, ...forecastColumns];

    const historyColumn: TableColumn = {
      key: 'history',
      header: 'History',
      width: '4%',
      align: 'center',
      editable: false,
      formatter: (_value, row, index) => {
        if (index === 0) return '-';
        return row.id || row.projectId ? 'show' : '-';
      },
      cellClass: (_value, _row, index) => index === 0 ? 'total-row' : 'history-cell',
    };

    this.tableColumns.push(historyColumn);
  }

  viewChangeLog(row: any): void {
    const oplReportingId = row.id || row.projectId;
    if (!oplReportingId) return;

    const columns: TableColumn[] = [
      {
        key: 'historyChangedAtUtc',
        header: 'Date',
        formatter: (value) => this.formatDate(value),
        align: 'left',
      },
      { key: 'historyChangedByUser', header: 'User' },
      { 
        key: 'isCurrent', 
        header: 'Status',
        formatter: (value) => value ? 'Current' : 'Archived',
        align: 'left',
      },
      { 
        key: 'value', 
        header: 'Value', 
        type: 'currency',
        align: 'right',
        formatter: (value) => this.formatCurrency(value)
      },
      { key: 'fiscalYear', header: 'Fiscal Year', align: 'left' },
      {
        key: 'projectVersion',
        header: 'Version',
        align: 'left',
        formatter: (_value, row) => {
          if (row.fiscalYear === undefined || !row.projectVersion) return _value;
          const baseFiscalYear = this.defaults.fiscalYear;
          const fiscalYear = row.fiscalYear;
          const projectVersion = row.projectVersion;
          if (projectVersion === 'BUDGET') {
            if (fiscalYear === baseFiscalYear) {
              return 'FC';
            } else if (fiscalYear === baseFiscalYear + 1) {
              return 'BUD';
            } else if (fiscalYear === baseFiscalYear + 2) {
              return 'PL1';
            } else if (fiscalYear === baseFiscalYear + 3) {
              return 'PL2';
            }
          }
          return _value;
        }
      },
    ];

    const config: ChangelogDialogConfig = {
      title: `OPL Reporting History: ${row.id}`,
      apiEndpoint: `api/inco/oplreporting/${oplReportingId}/history?fiscalYear=${this.defaults.fiscalYear}`,
      columns: columns,
      backButtonText: 'Back'
    };

    this.matDialog.open(ChangelogDialogComponent, {
      width: '95vw',
      data: config
    });
  }

  private formatDate(date: any): string {
    if (!date) return '-';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '-';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }

  private updateTitle(): void {
    const fyLabel = this.formatFiscalYearLabel(this.defaults.fiscalYear);
    this.title = `INCO: OPL Planning (${this.defaults.managementName} - FY ${fyLabel})`;
  }

  private formatFiscalYearLabel(fy: number): string {
    const startYear = (fy - 1) % 100;
    const endYear = fy % 100;
    return `${startYear.toString().padStart(2, '0')}/${endYear.toString().padStart(2, '0')}`;
  }

  private lastLoadedParams: string = '';

  loadData(showLoading: boolean = true, force: boolean = false): void {
    const currentParams = `${this.defaults.managementid}-${this.defaults.fiscalYear}`;
    if (!force && currentParams === this.lastLoadedParams) {
      return;
    }
    this.lastLoadedParams = currentParams;

    if (showLoading) {
      this.isLoading = true;
    }
    this.verificationStatus = '';

    this.incoService.loadOplProjects(
      this.defaults.managementid,
      this.defaults.fiscalYear,
      this,
      (tthis: IncoOplreportingComponent, result: IncoOplReportingResponse) => {
        tthis.periods = result.periods;
        tthis.projects = result.projects;
        tthis.sapFcTotal = result.sapFcTotal || 0;
        tthis.sapBudTotal = result.sapBudTotal || 0;
        tthis.sapPl1Total = result.sapPl1Total || 0;
        tthis.sapPl2Total = result.sapPl2Total || 0;
        tthis.currencyShort = result.currencyShort || 'EUR';
        tthis.currencySymbol = result.currencySymbol || '€';
        tthis.decimalDigits = 2;
        tthis.originalProjects = structuredClone(result.projects);
        tthis.initializeColumns();
        tthis.updateTitle();
        tthis.buildTableData();
        tthis.isLoading = false;
        tthis.updatedMeasures.clear();
        tthis.columnValidationStatus.clear();
      },
      (tthis: IncoOplreportingComponent, error: any) => {
        tthis.dialog.showErrorDialog(error);
        tthis.isLoading = false;
      }
    );
  }

  onClickEdit(): void {
    if (this.isEditModeEnabled) {
      this.projects = structuredClone(this.originalProjects);
      this.buildTableData();
      this.updatedMeasures.clear();
      this.isEditModeEnabled = false;
      this.isVerified = false;
      this.verificationStatus = '';
      this.columnValidationStatus.clear();
      this.initializeColumns();
    } else {
      this.isEditModeEnabled = true;
      this.isVerified = false;
      this.verificationStatus = '';
      this.columnValidationStatus.clear();
    }
  }

  onClickSave(): void {
    if (this.updatedMeasures.size === 0) {
      this.isEditModeEnabled = false;
      return;
    }

    const measures: IncoMeasureUpdateRequest[] = [];
    const currentFiscalYear = this.defaults.fiscalYear;
    
    this.updatedMeasures.forEach((data, _key) => {
      let targetFiscalYear: number;
      switch (data.forecastType) {
        case 'FC':
          targetFiscalYear = currentFiscalYear;
          break;
        case 'BUD':
          targetFiscalYear = currentFiscalYear + 1;
          break;
        case 'PL1':
          targetFiscalYear = currentFiscalYear + 2;
          break;
        case 'PL2':
          targetFiscalYear = currentFiscalYear + 3;
          break;
      }
      
      measures.push({
        projectId: data.projectId,
        value: data.value,
        mgmtId: this.defaults.managementid,
        fiscalYear: targetFiscalYear,
        period: 12,
        projectVersion: 'BUDGET',
        inputFiscalYear: currentFiscalYear
      });
    });

    this.incoService.saveMeasures(
      measures,
      this,
      (tthis: IncoOplreportingComponent, _result: any) => {
        tthis.toastrService.success('Values saved successfully!');
        tthis.isEditModeEnabled = false;
        tthis.isVerified = false;
        tthis.verificationStatus = '';
        tthis.loadData(false, true);
      },
      (tthis: IncoOplreportingComponent, error: any) => {
        tthis.dialog.showErrorDialog(error);
      }
    );
  }

  onCellValueChange(event: { row: any; column: TableColumn; value: any; rowIndex: number }): void {
    if (event.rowIndex === 0) return;

    const project = event.row as IncoOplMeasures;
    const originalProject = this.projects.find(p => p.id === project.id);
    if (!originalProject) return;

    this.handleValueChange(event.column.key, project.id, originalProject, event.value);

    if (this.isVerified) {
      this.isVerified = false;
      this.verificationStatus = '';
      this.columnValidationStatus.clear();
      this.initializeColumns();
    }
  }

  private handleValueChange(columnKey: string, projectId: number, originalProject: IncoOplMeasures, value: any): void {
    const forecastMap: Record<string, { prop: keyof IncoOplMeasures; type: OplForecastType }> = {
      fcValue: { prop: 'fcValue', type: 'FC' },
      budValue: { prop: 'budValue', type: 'BUD' },
      pl1Value: { prop: 'pl1Value', type: 'PL1' },
      pl2Value: { prop: 'pl2Value', type: 'PL2' },
    };

    const config = forecastMap[columnKey];
    if (!config) {
      return;
    }

    (originalProject[config.prop] as number | null) = value;

    this.updatedMeasures.set(
      `${projectId}_${config.type}`,
      { projectId, value, forecastType: config.type },
    );
  }

  private buildTableData(): void {
    const totalRow = {
      id: -1,
      projectId: 0,
      projectTitle: 'TOTAL',
      maintenance: null,
      expansion: null,
      phase: '-',
      isTotal: true,
      fcValue: null,
      budValue: null,
      pl1Value: null,
      pl2Value: null
    };
    
    this.tableData = [totalRow, ...this.projects];
  }

  getTotalFcValue(): number {
    return this.projects.reduce((sum, p) => sum + (p.fcValue || 0), 0);
  }

  getTotalBudValue(): number {
    return this.projects.reduce((sum, p) => sum + (p.budValue || 0), 0);
  }

  getTotalPl1Value(): number {
    return this.projects.reduce((sum, p) => sum + (p.pl1Value || 0), 0);
  }

  getTotalPl2Value(): number {
    return this.projects.reduce((sum, p) => sum + (p.pl2Value || 0), 0);
  }

  formatCurrency(value: number | null): string {
    if (value === null || value === undefined) {
      return '-';
    }
    return value.toLocaleString('de-DE', { 
      minimumFractionDigits: this.decimalDigits, 
      maximumFractionDigits: this.decimalDigits 
    });
  }

  private getValidationClass(status: 'valid' | 'invalid' | null | undefined): string | null {
    if (status === 'valid') {
      return 'validation-valid';
    }
    if (status === 'invalid') {
      return 'validation-invalid';
    }
    return null;
  }

  hasChanges(): boolean {
    return this.updatedMeasures.size > 0;
  }

  onClickVerify(): void {
    const totalFc = this.getTotalFcValue();
    const totalBud = this.getTotalBudValue();
    const totalPl1 = this.getTotalPl1Value();
    const totalPl2 = this.getTotalPl2Value();

    const sapFc = this.sapFcTotal;
    const sapBud = this.sapBudTotal;
    const sapPl1 = this.sapPl1Total;
    const sapPl2 = this.sapPl2Total;

    const fcMatch = TableUtils.amountsMatch(totalFc, sapFc);
    const budMatch = TableUtils.amountsMatch(totalBud, sapBud);
    const pl1Match = TableUtils.amountsMatch(totalPl1, sapPl1);
    const pl2Match = TableUtils.amountsMatch(totalPl2, sapPl2);

    this.columnValidationStatus.set('fcValue', fcMatch ? 'valid' : 'invalid');
    this.columnValidationStatus.set('budValue', budMatch ? 'valid' : 'invalid');
    this.columnValidationStatus.set('pl1Value', pl1Match ? 'valid' : 'invalid');
    this.columnValidationStatus.set('pl2Value', pl2Match ? 'valid' : 'invalid');

    const allMatch = fcMatch && budMatch && pl1Match && pl2Match;

    if (allMatch) {
      this.verificationStatus = 'success';
      this.isVerified = true;
      this.toastrService.success('All values match with SAP. You can now save.');
    } else {
      this.verificationStatus = 'error';
      this.isVerified = false;
      this.toastrService.warning('Some values do not match with SAP. Please check the highlighted columns.');
    }

    this.initializeColumns();
  }

  onClickExcel(): void {
    this.isExcelModeEnabled = true;
  }

  onClickDownload(): void {
    let date = new Date()
        const year = date.getFullYear().toString();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');

    Utils.httpDownloadIncoOplReporting(
      this.http,
      this.baseUrl,
      `${year}${month}${day}_${this.defaults.managementid}_${this.defaults.groupFunction}_${this.defaults.fiscalYear - 1}-${this.defaults.fiscalYear}_OPL`,
      this.defaults.managementid,
      this.defaults.fiscalYear,
      this,
      undefined,
      (tthis: any, error: any) => {
        this.toastrService.error('Error downloading file');
      }
    );
  }

  onClickUpload(): void {
    this.router.navigate(["file/" + Utils.getFileUploadIncoOplReportingInformation()]);
  }

  onClickExitExcel(): void {
    this.isExcelModeEnabled = false;
  }
}
